/**
 * THE LAST DIG, AT THE TOP OF WHAT IT WAS AIMED AT: when it ran, how it went,
 * and what it found, in one line, with the way to the full list.
 *
 * An item's or a table's page changed after a dig with nothing to say so -
 * "I ran another run and I can't really tell what it did". The full results
 * live in Recon Runs, under the dig's own row; this is the headline and the
 * link there (`recon_runs.latest`).
 */
import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type { BoardroomReconRun } from '../../../types';
import { feedTime, feedTimeTitle } from '../feedTime';
import { addedWords, tokenWords } from './ReconRunsReport';

/** The banner itself, from a run. Pure, so it can be tested without the API. */
export function DigSummary({
  run,
  onShowRun,
}: {
  run: BoardroomReconRun;
  /** Open Recon Runs at this dig, its findings showing. */
  onShowRun?: (runId: number) => void;
}) {
  const running = run.status === 'running';
  const found = Object.values(run.added ?? {}).reduce((a, n) => a + n, 0);
  return (
    <section
      className="shrink-0 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs dark:border-emerald-800 dark:bg-emerald-900/10"
      data-testid="last-dig"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-gray-700 dark:text-gray-200">
        <span className="font-medium text-emerald-900 dark:text-emerald-200">
          {running ? 'A dig is running here now' : 'Last dig into this'}
        </span>
        <span className="text-gray-500 dark:text-gray-400" title={feedTimeTitle(run.at)}>
          {running ? `started ${feedTime(run.at)}` : feedTime(run.ended_at ?? run.at)}
        </span>
        <span className="text-gray-400">·</span>
        <span>{run.intent}</span>
        <span className="text-gray-400">·</span>
        <span>
          {run.turns} turn{run.turns === 1 ? '' : 's'}, {tokenWords(run.tokens)} tokens
        </span>
        {!running && run.ended && (
          <>
            <span className="text-gray-400">·</span>
            <span>{run.ended}</span>
          </>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-gray-700 dark:text-gray-200">
        <span data-testid="last-dig-found">
          {found ? `${running ? 'Found so far' : 'Found'}: ${addedWords(run.added, 6)}` : running ? 'Nothing found yet' : 'It found nothing new'}
        </span>
        {found > 0 && onShowRun && (
          <button
            type="button"
            onClick={() => onShowRun(run.id)}
            className="text-emerald-700 hover:underline dark:text-emerald-300"
            data-testid="last-dig-show"
          >
            see everything it found →
          </button>
        )}
      </div>
    </section>
  );
}

/** Fetches the latest dig at one item or one table and shows `DigSummary`,
 *  or nothing when it has never been dug into. */
export default function LastDigSummary({
  boardroomId,
  itemId,
  table,
  refreshKey,
  onShowRun,
}: {
  boardroomId: number;
  itemId?: number;
  table?: string;
  /** Changes when the page reloads, so the summary follows a running dig. */
  refreshKey?: unknown;
  onShowRun?: (runId: number) => void;
}) {
  const [run, setRun] = useState<BoardroomReconRun | null>(null);
  // Another item or table: never show the last one's dig while this one loads.
  useEffect(() => setRun(null), [boardroomId, itemId, table]);
  useEffect(() => {
    let alive = true;
    api
      .getBoardroomLatestDig(boardroomId, { itemId, table })
      .then(r => {
        if (alive) setRun(r);
      })
      .catch(() => {
        if (alive) setRun(null);
      });
    return () => {
      alive = false;
    };
  }, [boardroomId, itemId, table, refreshKey]);
  // WHILE IT RUNS, THE STATUS BAR SAYS IT: started when, turns, what it has
  // found so far. Two boxes saying the same thing was one too many; this one
  // is the record once it has ended.
  if (!run || run.status === 'running') return null;
  return <DigSummary run={run} onShowRun={onShowRun} />;
}
